import * as React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { useTheme } from '@mui/material/styles'
import { format } from 'date-fns'
import { moreEventsLabel } from '../../dateFormat'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'

type Props = {
  count: number
  day: Date
  /** Opens the day view; without it "+N more" is a plain note. */
  onShowDay?: (day: Date) => void
}

/** The "+N more" row that stands in for all-day events a day has no room for. */
export function AllDayMore({ count, day, onShowDay }: Props) {
  const theme = useTheme()

  const moreSx = {
    justifyContent: 'flex-start',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: theme.typography.fontFamily,
    fontSize: '12px',
    fontWeight: 600,
    color: theme.palette.primary.main,
  } as const

  if (!onShowDay) {
    return (
      <Box
        data-test-id="all-day-more"
        role="note"
        aria-label={moreEventsLabel(count)}
        sx={{ ...moreSx, display: 'flex' }}
      >
        +{count} more
      </Box>
    )
  }

  return (
    <ButtonBase
      data-test-id="all-day-more"
      onClick={() => onShowDay(day)}
      aria-label={`${moreEventsLabel(count)} on ${format(day, 'MMMM d')}, open day view`}
      sx={{
        ...moreSx,
        [COARSE_POINTER]: { minHeight: TOUCH_TARGET_PX },
        '&:hover': { backgroundColor: theme.palette.grey[100] },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 1,
        },
      }}
    >
      +{count} more
    </ButtonBase>
  )
}

AllDayMore.displayName = 'AllDayMore'
